import { AppShell, cn, type NavEntry } from '@minisoc/ui';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ApiError } from '@minisoc/api-client';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { api } from './api';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { LoginPage } from './auth/LoginPage';
import { useRealtimeAlerts } from './hooks/useRealtimeAlerts';
import { AlertDetailPage } from './pages/AlertDetailPage';
import { AlertsPage } from './pages/AlertsPage';
import { AssistantPage } from './pages/AssistantPage';
import { KpiPage } from './pages/KpiPage';
import { RecommendationsPage } from './pages/RecommendationsPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: true,
      // Une erreur d'authentification ou d'autorisation ne se résout pas par une
      // nouvelle tentative : seul l'aléa réseau le justifie.
      retry: (failureCount, error) =>
        !(error instanceof ApiError && error.status < 500) && failureCount < 2,
    },
  },
});

const NAV: NavEntry[] = [
  { label: 'Alertes', to: '/alertes' },
  { label: 'KPI / Analytics', to: '/kpi' },
  { label: 'Recommandations', to: '/recommandations' },
  { label: 'Assistant IA', to: '/assistant' },
];

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Gate />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function Gate() {
  const { user } = useAuth();
  return user ? <AuthenticatedApp /> : <LoginPage />;
}

function AuthenticatedApp() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { connected } = useRealtimeAlerts();

  // Badge du nombre d'alertes ouvertes dans la navigation : c'est l'information
  // qui doit rester visible depuis n'importe quel onglet.
  const kpi = useQuery({ queryKey: ['kpi', 'nav'], queryFn: () => api.getKpiDashboard(30) });

  const nav = NAV.map((entry) =>
    entry.to === '/alertes' ? { ...entry, badge: kpi.data?.counters.open } : entry,
  );

  return (
    <AppShell
      nav={nav}
      currentPath={location.pathname}
      onNavigate={(to) => navigate(to)}
      footer={
        <div className="space-y-2">
          <div>
            <p className="truncate font-medium text-text">{user?.tenantName ?? '—'}</p>
            <p className="truncate text-text-dim">{user?.email}</p>
          </div>

          <p className="flex items-center gap-1.5 text-text-dim">
            <span
              className={cn(
                'inline-block size-1.5 rounded-full',
                connected ? 'bg-success' : 'bg-high',
              )}
            />
            {connected ? 'Temps réel actif' : 'Reconnexion…'}
          </p>

          <button
            type="button"
            onClick={() => void logout()}
            className="text-text-muted transition-colors hover:text-critical"
          >
            Se déconnecter
          </button>
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<Navigate to="/alertes" replace />} />
        <Route path="/alertes" element={<AlertsPage />} />
        <Route path="/alertes/:id" element={<AlertDetailPage />} />
        <Route path="/kpi" element={<KpiPage />} />
        <Route path="/recommandations" element={<RecommendationsPage />} />
        <Route path="/assistant" element={<AssistantPage />} />
        <Route path="*" element={<Navigate to="/alertes" replace />} />
      </Routes>
    </AppShell>
  );
}
